import _defineProperty from "/workdir/node_modules/next/node_modules/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable react/no-multi-comp, react/display-name, react/prop-types */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import RemarkBreaks from 'remark-breaks';
import RemarkGFM from 'remark-gfm';
import commentMarker from 'mdast-comment-marker';
import visit from 'unist-util-visit';
import Paragraph from "@scentregroup/ui/components/paragraph";
import Heading from "@scentregroup/ui/components/heading";
import { startsWith, deburr, get, kebabCase } from 'lodash';
import ResponsiveImage from '../responsive-image';
import { ProductsInMarkdown } from '../products-in-markdown';
import Link from '../link';
import style from './index.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_MARKDOWN_IMAGE_WIDTH = 1200; // Take an inline html comment like so:
//
// <!--products align=left feature-->
//
// And parse it as
//
// <!--[renderer] [...parameters]-->
//
// Where `renderer` is a react-markdown renderer, and ...parameters is an object
// passed to the renderer.
//
// NOTE: a parameter without a value (`feature` in the above example) will have
// its value set to `true`.
//
// NOTE 2: Any magic comment which does not match the given renderer name will
// be passed through unchanged. It's advised to use react-markdown's `skipHtml`
// prop to avoid it being emitted as a raw HTML comment.
//
// Example
// Given the above comment, usage might look like:
//
//  <ReactMarkdown
//    skipHtml
//    astPlugins={[magicComments('products')]}
//    renderers={{
//      products: ({ parameters }) => (
//        <ProductList align={parameters.align || 'center'} isFeature={parameters.feature} />
//      )
//    }}
//  />

const magicComments = renderer => ast => {
  // Walk to each "html" node and modify it inline
  visit(ast, 'html', node => {
    // Attempt to extract the special comment syntax from the node
    const marker = commentMarker(node);

    if (marker && marker.name === renderer) {
      // Force the type to match the renderer
      node.type = renderer; // Pass through the parsed parameters

      node.parameters = marker.parameters;
    }
  }); // Have to return the "new" ast (which in this case is just the existing ast
  // object with modified data)

  return ast;
};

const renderers = ({
  dataTestId,
  paragraphType
}) => ({
  root: ({
    children
  }) => /*#__PURE__*/_jsx("div", {
    className: style.Markdown,
    "data-test-id": dataTestId,
    children: children
  }),
  link: ({
    title,
    href,
    children
  }) => startsWith(href, '#') ? /*#__PURE__*/_jsx("a", {
    href: href,
    title: title,
    children: children
  }) : /*#__PURE__*/_jsx(Link, {
    href: href,
    title: title,
    children: children
  }),
  paragraph: ({
    children
  }) => /*#__PURE__*/_jsx(Paragraph, {
    type: paragraphType,
    children: children
  }),
  heading: ({
    level,
    children
  }) => /*#__PURE__*/_jsx(Heading, {
    type: {
      1: 'large',
      2: 'medium',
      3: 'small'
    }[level] || 'extrasmall',
    id: kebabCase(deburr(get(children, '[0].props.value', ''))),
    children: children
  }),
  image: ({
    src,
    alt
  }) => /*#__PURE__*/_jsx(ResponsiveImage, {
    lazyLoad: true,
    desktopImageUrl: src,
    mobileImageUrl: src,
    width: DEFAULT_MARKDOWN_IMAGE_WIDTH,
    alt: alt
  }),
  tableCell: ({
    isHeader,
    children
  }) => isHeader ? /*#__PURE__*/_jsx("th", {
    children: /*#__PURE__*/_jsx(Heading, {
      type: "extrasmall",
      children: children
    })
  }) : /*#__PURE__*/_jsx("td", {
    children: children
  }),
  products: ({
    parameters = {}
  }) => /*#__PURE__*/_jsx(ProductsInMarkdown, {
    ids: parameters.ids
  })
}); // https://github.com/rexxars/react-markdown/blob/8ad8a995024a4729f066d109382b10c47d513379/src/renderers.js#L8


const Markdown = ({
  content,
  dataTestId,
  paragraphType = 'large',
  customRenderers = _ => ({})
}) => {
  const defaultRenderers = renderers({
    dataTestId,
    paragraphType
  });
  return /*#__PURE__*/_jsx(ReactMarkdown, {
    skipHtml: true,
    source: content,
    plugins: [RemarkBreaks, RemarkGFM] // NOTE: has to be an astPlugin as it visits the ast after it's parsed, and
    // makes modifications before being given to the AST-to-react tree walker
    ,
    astPlugins: [magicComments('products')],
    className: style.Markdown,
    renderers: _objectSpread(_objectSpread({}, defaultRenderers), customRenderers(defaultRenderers))
  });
};

export default Markdown;